import { Component, OnInit, signal } from '@angular/core'
import { CommonModule } from '@angular/common'
import {
  ReactiveFormsModule,
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms'
import { Router, RouterLink } from '@angular/router'
import { HttpClient } from '@angular/common/http'

import { environment } from '../../../environments/environment'
import { AuthService } from '../../abacus/services/auth.service'
import { User } from '../../abacus/interfaces/resources/user.interface'
import { StepProgressBarComponent } from '../../shared/components/step-progress-bar/step-progress-bar.component'
import { oecdCountries } from '../../shared/data/oecd-countries'

@Component({
  templateUrl: './transparency-register.component.html',
  styleUrls: ['./transparency-register.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    StepProgressBarComponent
  ]
})
export class TransparencyRegisterComponent implements OnInit {
  readonly steps = new Map<number, string>([
    [1, 'Account'],
    [2, 'Organization'],
    [3, 'Summary']
  ])

  readonly countries = oecdCountries
  currentStep = 1
  loading = signal(false)
  submitted = signal(false)
  errorMessage = signal<string | null>(null)

  form: FormGroup

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private authService: AuthService,
    private router: Router
  ) {
    this.form = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.pattern(
            /(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{}|;:'",.<>?/\\`~])/
          )
        ]
      ],
      confirmPassword: ['', Validators.required],
      organizationName: ['', Validators.required],
      country: ['', Validators.required],
      addressLine1: [''],
      addressLine2: [''],
      city: [''],
      state: [''],
      zip: [''],
      jobTitle: [''],
      phoneNumber: [''],
      website: ['', [Validators.required, this.urlValidator]]
    }, { validators: this.passwordsMatch })
  }

  ngOnInit(): void {
    if (this.authService.isLoggedIn()) {
      this.authService.me().subscribe((user) => {
        const homepagePath = (user as User)?.role?.homepagePath
        this.router.navigate([homepagePath || '/'])
      })
    }
  }

  get step1Valid(): boolean {
    return (
      this.form.get('firstName')!.valid &&
      this.form.get('lastName')!.valid &&
      this.form.get('email')!.valid &&
      this.form.get('password')!.valid &&
      this.form.get('confirmPassword')!.valid &&
      !this.form.hasError('passwordsMismatch')
    )
  }

  get step2Valid(): boolean {
    return (
      this.form.get('organizationName')!.valid &&
      this.form.get('country')!.valid &&
      this.form.get('website')!.valid
    )
  }

  goToStep(step: number): void {
    if (step < this.currentStep) {
      this.currentStep = step
      return
    }
    if (step === 2 && !this.step1Valid) {
      this.markStep1Touched()
      return
    }
    if (step === 3) {
      if (!this.step1Valid) {
        this.markStep1Touched()
        return
      }
      if (!this.step2Valid) {
        this.markStep2Touched()
        return
      }
    }
    this.currentStep = step
  }

  continueToNext(): void {
    if (this.currentStep === 1) {
      if (!this.step1Valid) {
        this.markStep1Touched()
        return
      }
      this.currentStep = 2
    } else if (this.currentStep === 2) {
      if (!this.step2Valid) {
        this.markStep2Touched()
        return
      }
      this.currentStep = 3
    }
  }

  getCountryLabel(code: string): string {
    return this.countries.find((c) => c.value === code)?.label || code
  }

  submit(): void {
    if (!this.step1Valid || !this.step2Valid) return

    this.loading.set(true)
    this.errorMessage.set(null)

    const { confirmPassword, ...payload } = this.form.value
    this.http
      .post(`${environment.apiBaseUrl}/auth/register-transparency-report-user`, payload)
      .subscribe({
        next: () => {
          this.loading.set(false)
          this.submitted.set(true)
        },
        error: (err) => {
          this.loading.set(false)
          const msg =
            err.error?.message ||
            'Registration failed. Please try again.'
          this.errorMessage.set(Array.isArray(msg) ? msg.join('. ') : msg)
        }
      })
  }

  private passwordsMatch(group: AbstractControl): ValidationErrors | null {
    const password = group.get('password')!.value
    const confirm = group.get('confirmPassword')!.value
    return password === confirm ? null : { passwordsMismatch: true }
  }

  private markStep1Touched(): void {
    const fields = ['firstName', 'lastName', 'email', 'password', 'confirmPassword']
    fields.forEach((key) => this.form.get(key)!.markAsTouched())
  }

  onWebsiteBlur(): void {
    const control = this.form.get('website')!
    const value = (control.value || '').trim()
    if (value && !/^https?:\/\//i.test(value)) {
      control.setValue(`https://${value}`)
    }
  }

  private urlValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value
    if (!value) return null
    if (/\s/.test(value)) return { url: true }
    try {
      const url = new URL(value)
      if (!['http:', 'https:'].includes(url.protocol)) return { url: true }
      const parts = url.hostname.split('.')
      if (parts.length < 2) return { url: true }
      const tld = parts[parts.length - 1]
      if (!tld || tld.length < 2 || /^\d+$/.test(tld)) return { url: true }
      return null
    } catch {
      return { url: true }
    }
  }

  private markStep2Touched(): void {
    const fields = ['organizationName', 'country', 'website']
    fields.forEach((key) => this.form.get(key)!.markAsTouched())
  }
}
